import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'redux/store';

import { styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Logout } from '@mui/icons-material';

import { appConfig } from 'utils/config';

import { signOut } from 'redux/slices/auth';

// -------------------- styled components --------------------

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '252px',
  padding: '8px',
  borderRadius: '8px',
  backgroundColor: theme.palette.neutralBackground.main,
}));

// ------------------ component ---------------------

const MenuItem = ({ icon, title, onClick }) => {
  return (
    <Box
      p={1}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          bgcolor: '#DFDFDF',
          borderRadius: '6px',
          transition: 'all 0.3s',

          '& p': {
            color: 'primaryBrandPurwadhika.main',
          },
        },
      }}
      onClick={onClick}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          typography={{ xs: 'paragraph2', md: 'bodytext16' }}
          color="neutral.main"
          sx={{ transition: 'all 0.2s' }}
        >
          {title}
        </Typography>
        {icon}
      </Stack>
    </Box>
  );
};

const ProfileUser = ({
  closeMenu,
  email,
  firstName,
  lastName,
  profilePicture,
}) => {
  const { baseUrl } = appConfig;

  return (
    <Link
      to="/profile"
      style={{ textDecoration: 'none', color: 'initial' }}
      onClick={closeMenu}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing="8.75px"
        p={1}
        sx={{
          '& .navbar-profile-username': {
            transition: 'all 0.2s',
          },

          '&:hover': {
            bgcolor: '#DFDFDF',
            borderRadius: '6px',
            transition: 'all 0.3s',

            '& .navbar-profile-username': {
              color: 'primaryBrandPurwadhika.main',
            },
          },
        }}
      >
        <Avatar
          src={
            profilePicture !== '/defaultprofilepicture.png'
              ? baseUrl + profilePicture
              : 'static/icons/ic_profile.svg'
          }
          sx={{ width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 } }}
        />
        <Stack sx={{ width: '100%' }} minWidth={0}>
          <Typography
            className="navbar-profile-username"
            typography={{ xs: 'subheading4', md: 'bodytextbold16' }}
          >
            {firstName} {lastName}
          </Typography>

          <Typography
            variant="captionreguler"
            color="#747474"
            sx={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {email}
          </Typography>
        </Stack>
      </Stack>
    </Link>
  );
};

const ListItem = ({ closeMenu, menuItems }) => {
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));

  return (
    <Stack px={1} spacing={{ xs: 0.5, md: 1 }} alignitems="center">
      {React.Children.toArray(
        menuItems.map((item) => {
          if (item.onlyMobile && !isMobile) {
            return null;
          }

          if (item.action) {
            return (
              <MenuItem
                title={item.title}
                onClick={item.action}
                icon={item.icon}
              />
            );
          }

          return (
            <Link
              to={item.path}
              style={{ textDecoration: 'none' }}
              onClick={closeMenu}
            >
              <MenuItem title={item.title} icon={item.icon} />
            </Link>
          );
        }),
      )}
    </Stack>
  );
};

const LogOut = ({ closeMenu }) => {
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(signOut());

    closeMenu();
  };

  return (
    <Box px={1}>
      <MenuItem
        title="Log Out"
        onClick={handleLogout}
        icon={
          <Logout
            color="neutral500"
            sx={{ fontSize: { xs: '20px', md: '24px' } }}
          />
        }
      />
    </Box>
  );
};

const MenuUserItem = ({ user, logOut, closeMenu }) => {
  const notification = useSelector((state) => state.notification);

  const menuItems = useMemo(
    () => [
      {
        title: 'My Account',
        path: '/profile',
      },
      {
        title: 'Notification',
        path: '/notification',
        onlyMobile: true,
        icon: (
          <Box p="4px 8px" bgcolor="error500.main" borderRadius="40px">
            <Typography variant="metatag1" color="error300.main">
              {notification?.filteredNotifications?.length}
            </Typography>
          </Box>
        ),
      },
      {
        title: 'Back to Home',
        action: () => {
          window.location.href = window.location.origin;
        },
      },
    ],
    [notification?.filteredNotifications?.length],
  );

  return (
    <BoxContainer>
      <ProfileUser
        closeMenu={closeMenu}
        email={user?.email}
        lastName={user?.lastName}
        firstName={user?.firstName}
        profilePicture={user?.profilePicture}
      />

      <Divider sx={{ m: { xs: 0.5, md: 1 }, bgcolor: 'neutral600.main' }} />

      <ListItem menuItems={menuItems} closeMenu={closeMenu} />

      <Divider sx={{ m: { xs: 0.5, md: 1 }, bgcolor: 'neutral600.main' }} />

      <LogOut logOut={logOut} closeMenu={closeMenu} />
    </BoxContainer>
  );
};

export default MenuUserItem;
